import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Presentation, MessageSquareWarning, Handshake, CalendarCheck, Mic, RefreshCcw, DoorOpen } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const PITCHPREP_URL = "https://www.pitchprep.co.in";

const EASE = [0.22, 1, 0.36, 1];

const OFFERS = [
  {
    icon: Presentation,
    title: "Structured Practice",
    text: "Rehearse your pitch end-to-end with guided, repeatable practice sessions.",
  },
  {
    icon: MessageSquareWarning,
    title: "Blunt Feedback",
    text: "No polite nodding. A page-by-page teardown of your story, your numbers, and your delivery.",
  },
  {
    icon: Handshake,
    title: "Tough-Question Drills",
    text: "Prepare for the challenging questions that come up in real investor conversations.",
  },
];

const STEPS = [
  { icon: CalendarCheck, title: "Book a Rep", text: "Pick a slot and share your deck." },
  { icon: Mic, title: "Pitch in a Safe Room", text: "Rehearse the full pitch without stakes." },
  { icon: RefreshCcw, title: "Get Torn Apart", text: "Blunt, specific, usable feedback." },
  { icon: DoorOpen, title: "Step In Ready", text: "Walk into the real room with reps behind you." },
];

export default function PitchPrep() {
  return (
    <div className="min-h-screen bg-nexlight text-nexink font-sans" data-testid="pitchprep-page">
      <Seo
        title="PitchPrep — Rehearse Your Raise | NexOrbit Solutions"
        siteName="NexOrbit Solutions"
        description="PitchPrep helps founders practise their investor pitch, refine their story and prepare for challenging questions — structured practice and feedback, currently in pilot."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "PitchPrep",
          description: "Structured practice and feedback for founders preparing to pitch investors.",
          brand: { "@type": "Organization", name: "NexOrbit Solutions LLP" },
        }}
      />

      <header className="sticky top-0 z-50 border-b border-[#072B5B]/10 bg-white/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <a
            href={PITCHPREP_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="PitchPrep home"
            data-testid="pitchprep-logo-link"
          >
            <img src="/assets/pitchprep-wordmark.png" alt="PitchPrep — Better Pitches. Bigger Dreams." className="h-11 w-auto" />
          </a>
          <a
            href={PITCHPREP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pitchprep-nav-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#E6631F] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.04]"
          >
            Visit PitchPrep
            <ArrowUpRight size={16} />
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#072B5B] py-24 sm:py-32">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="orbit-ring w-[600px] h-[600px] -right-40 -top-40 animate-spin-slow" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(230,99,31,0.10),transparent_55%)]" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-10 bg-[#E6631F]" />
              <span className="text-xs font-semibold tracking-[0.35em] text-nexcyan">BETTER PITCHES. BIGGER DREAMS.</span>
            </motion.div>

            <h1 className="font-display font-bold tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }}>
                  <a
                    href={PITCHPREP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pitchprep-hero-link"
                    className="text-[#E6631F] underline decoration-nexcyan/60 decoration-4 underline-offset-8 transition-colors hover:text-nexcyan"
                  >
                    PitchPrep
                  </a>
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.35, ease: EASE }}>
                  Rehearse Your <span className="text-nexcyan">Raise.</span>
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
              data-testid="pitchprep-subheading"
            >
              Practise your investor pitch, refine your story and prepare for challenging questions. Build confidence through structured practice and feedback before your next investor conversation.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href={PITCHPREP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pitchprep-try-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-[#E6631F] px-7 py-3.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.05] hover:shadow-[0_12px_32px_rgba(230,99,31,0.4)]"
              >
                Join the Pilot
                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#E6631F]/60 bg-[#E6631F]/10 px-4 py-3 text-xs font-bold tracking-[0.2em] text-[#E6631F]" data-testid="pitchprep-pilot-badge">
                IN PILOT
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-sm text-white/70">
                A NexOrbit Solutions Product
              </span>
            </motion.div>

            <motion.a
              href={PITCHPREP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pitchprep-hero-mark"
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="hidden lg:block absolute right-8 top-28 rounded-3xl bg-white p-8 shadow-[0_30px_60px_rgba(0,0,0,0.35)] transition-transform duration-300 hover:scale-[1.04]"
            >
              <img src="/assets/pitchprep-mark.png" alt="PitchPrep logo mark" className="w-40 h-auto" />
            </motion.a>
          </div>
        </section>

        <section className="py-24 sm:py-28" data-testid="pitchprep-offers-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal className="max-w-3xl">
              <div className="flex items-center gap-4 mb-5">
                <span className="font-display text-sm font-bold text-[#E6631F]">01</span>
                <span className="h-px w-12 bg-[#072B5B]/25" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-nexcyan">What You Get</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#072B5B]" data-testid="pitchprep-offers-heading">
                Everything Between You and the Cheque.
              </h2>
            </Reveal>
            <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
              {OFFERS.map(({ icon: Icon, title, text }) => (
                <StaggerItem key={title}>
                  <div
                    data-testid={`pitchprep-offer-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group h-full rounded-2xl border border-nexblue/10 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:border-nexorange/50"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#072B5B] text-[#E6631F] transition-colors duration-300 group-hover:bg-[#E6631F] group-hover:text-[#072B5B]">
                      <Icon size={22} />
                    </div>
                    <h3 className="mt-5 font-display text-xl font-semibold text-[#072B5B]">{title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-nexink/70">{text}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section className="py-24 sm:py-28 bg-white" data-testid="pitchprep-how-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal className="max-w-3xl">
              <div className="flex items-center gap-4 mb-5">
                <span className="font-display text-sm font-bold text-[#E6631F]">02</span>
                <span className="h-px w-12 bg-[#072B5B]/25" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-nexcyan">How It Works</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#072B5B]" data-testid="pitchprep-how-heading">
                All Reps. No Regrets.
              </h2>
            </Reveal>
            <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {STEPS.map(({ icon: Icon, title, text }, i) => (
                <StaggerItem key={title}>
                  <div className="relative h-full rounded-2xl border border-nexblue/10 bg-nexlight p-7" data-testid={`pitchprep-step-${i + 1}`}>
                    <span className="absolute right-5 top-3 font-display text-5xl font-bold text-[#072B5B]/[0.06]" aria-hidden="true">0{i + 1}</span>
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-nexcyan/10 text-nexcyan">
                      <Icon size={20} />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold text-[#072B5B]">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-nexink/70">{text}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section className="py-24 sm:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl bg-[#072B5B] px-8 py-14 sm:px-14 text-center" data-testid="pitchprep-cta-banner">
                <div className="orbit-ring w-[420px] h-[420px] -left-32 -bottom-32" style={{ borderColor: "rgba(230,99,31,0.15)" }} aria-hidden="true" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,168,232,0.10),transparent_55%)]" aria-hidden="true" />
                <h2 className="relative font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
                  Step Into the Room Ready
                </h2>
                <p className="relative mt-4 text-base text-white/65 max-w-xl mx-auto">
                  <a href={PITCHPREP_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#E6631F] hover:text-nexcyan transition-colors" data-testid="pitchprep-banner-link">
                    PitchPrep
                  </a>{" "}
                  is currently in pilot — prepare, practise and refine your investor pitch with early access.
                </p>
                <a
                  href={PITCHPREP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pitchprep-banner-cta"
                  className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-[#E6631F] px-8 py-4 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.05] hover:shadow-[0_12px_32px_rgba(230,99,31,0.4)]"
                >
                  Join the Pilot at pitchprep.co.in
                  <ArrowUpRight size={17} />
                </a>
              </div>
            </Reveal>
            <div className="mt-10 text-center">
              <a href="/" className="inline-flex items-center gap-2 text-sm font-semibold text-nexink/50 transition-colors hover:text-nexcyan" data-testid="pitchprep-back-home">
                <ArrowLeft size={15} /> Back to NexOrbit Solutions
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
